// NGAPRulesPanel — Panneau de règles NGAP interactif
// Explique en direct pourquoi chaque acte est coté à tel taux.
// Liste d'actes cliquable (gauche) + détail du raisonnement (droite).

import { useState } from 'react';
import type { BSIResult, ActeAnalyse } from '../intelligence/types';

interface NGAPRulesPanelProps {
  bsiResult: BSIResult;
}

type RateFilter = 'tous' | '100' | '50' | '0' | 'inclus';

interface RateInfo {
  label: string;
  short: string;
  badge: string;
  dot: string;
}

const PERIOD_META: Record<string, { emoji: string; text: string; border: string }> = {
  matin: { emoji: '🌅', text: 'text-amber-700', border: 'border-amber-200/60' },
  midi: { emoji: '☀️', text: 'text-orange-700', border: 'border-orange-200/60' },
  soir: { emoji: '🌙', text: 'text-indigo-700', border: 'border-indigo-200/60' },
};

function rateInfo(act: ActeAnalyse): RateInfo {
  if (act.inclusDansForfait) {
    return {
      label: 'Inclus',
      short: 'Déjà couvert par le forfait',
      badge: 'bg-foreground-100 text-foreground-400',
      dot: 'bg-foreground-400',
    };
  }
  if (act.tauxApplique === 0) {
    return {
      label: 'Gratuit',
      short: 'Non facturé',
      badge: 'bg-foreground-100 text-foreground-400',
      dot: 'bg-foreground-400',
    };
  }
  if (act.codeNGAP.includes('AMX')) {
    if (act.tauxApplique >= 100) {
      return { label: '100% AMX', short: 'Dérogatoire à taux plein', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
    }
    if (act.tauxApplique >= 50) {
      return { label: '50% AMX', short: 'Acte technique sous BSI', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
    }
    return { label: `${act.tauxApplique}% AMX`, short: 'Acte technique sous BSI', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
  }
  if (act.tauxApplique >= 100) {
    return { label: '100%', short: 'Taux plein', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
  }
  if (act.tauxApplique >= 50) {
    return { label: '50%', short: 'Décoté (Art. 11B)', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
  }
  return { label: `${act.tauxApplique}%`, short: 'Décoté', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
}

/** Déduit une courte phrase de décision à partir des drapeaux de l'acte. */
function decisionPhrase(act: ActeAnalyse): string {
  if (act.inclusDansForfait) {
    return 'Cet acte est inclus dans le forfait de dépendance — il ne génère aucune facturation séparée.';
  }
  if (act.codeNGAP.includes('AMX')) {
    if (act.tauxApplique >= 100) {
      return 'Acte dérogatoire : sous BSI, il reste coté en AMX à taux plein.';
    }
    return 'Sous BSI, cet acte technique non dérogatoire est converti en AMX à 50% du coefficient.';
  }
  if (act.tauxApplique === 0) {
    return 'Cet acte arrive en 3e position (ou au-delà) dans son passage — la règle de cumul 100/50/0 le rend gratuit.';
  }
  if (act.tauxApplique >= 50 && act.tauxApplique < 100) {
    return 'Cet acte est le 2e de son passage — la règle de cumul (Art. 11B) le facture à 50%.';
  }
  return 'Cet acte est le 1er de son passage (ou bénéficie d\'une immunité/dérogation) — facturé à taux plein.';
}

/** Étapes visuelles du pipeline de décision. */
function buildTrace(act: ActeAnalyse): { titre: string; detail: string; icon: string; color: string }[] {
  const trace = [
    { titre: 'Détection', detail: `${act.codeNGAP} — ${act.acte.acte.label}`, icon: 'ri-search-eye-line', color: 'text-primary-600' },
  ];

  if (act.inclusDansForfait) {
    trace.push({
      titre: 'Absorption forfait',
      detail: 'Acte couvert par le forfait de dépendance — jamais facturé en plus.',
      icon: 'ri-archive-line',
      color: 'text-foreground-500',
    });
  } else if (act.codeNGAP.includes('AMX')) {
    trace.push({
      titre: 'Conversion AMX',
      detail: act.tauxApplique >= 100
        ? 'Acte dérogatoire — AMX à taux plein.'
        : 'Acte technique non dérogatoire — AMX à 50%.',
      icon: 'ri-exchange-line',
      color: 'text-secondary-600',
    });
  } else if (act.tauxApplique < 100) {
    trace.push({
      titre: 'Cumul 100/50/0',
      detail: act.tauxApplique === 0
        ? '3e acte et suivants → gratuit (Art. 11B).'
        : '2e acte → décoté à 50% (Art. 11B).',
      icon: 'ri-git-merge-line',
      color: 'text-amber-600',
    });
  } else {
    trace.push({
      titre: 'Taux plein',
      detail: '1er acte ou immunité (Art. 5bis / Art. 7 / perfusion).',
      icon: 'ri-check-double-line',
      color: 'text-emerald-600',
    });
  }

  trace.push({
    titre: 'Résultat',
    detail: `${rateInfo(act).label} — ${act.montantFinal.toFixed(2).replace('.', ',')} €`,
    icon: 'ri-flag-2-line',
    color: act.inclusDansForfait || act.tauxApplique === 0 ? 'text-foreground-500' : 'text-accent-600',
  });

  return trace;
}

function matchesFilter(act: ActeAnalyse, filter: RateFilter): boolean {
  switch (filter) {
    case 'tous': return true;
    case 'inclus': return act.inclusDansForfait;
    case '0': return !act.inclusDansForfait && act.tauxApplique === 0;
    case '50': return !act.inclusDansForfait && act.tauxApplique > 0 && act.tauxApplique < 100;
    case '100': return !act.inclusDansForfait && act.tauxApplique >= 100;
  }
}

const FILTERS: { id: RateFilter; label: string }[] = [
  { id: 'tous', label: 'Tous' },
  { id: '100', label: '100%' },
  { id: '50', label: '50%' },
  { id: '0', label: 'Gratuit' },
  { id: 'inclus', label: 'Inclus' },
];

export default function NGAPRulesPanel({ bsiResult }: NGAPRulesPanelProps) {
  const [filter, setFilter] = useState<RateFilter>('tous');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const actes = bsiResult.actes;
  const filtered = actes.filter((a) => matchesFilter(a, filter));

  const keyOf = (a: ActeAnalyse) => `${a.acte.periode}-${a.acte.acte.id}`;
  const selected = actes.find((a) => keyOf(a) === selectedId) ?? filtered[0] ?? actes[0];

  const periodOrder = ['matin', 'midi', 'soir'];
  const grouped: Record<string, ActeAnalyse[]> = {};
  filtered.forEach((a) => {
    if (!grouped[a.acte.periode]) grouped[a.acte.periode] = [];
    grouped[a.acte.periode].push(a);
  });
  const sortedPeriods = periodOrder.filter((p) => grouped[p]);

  const countFor = (f: RateFilter) => f === 'tous' ? actes.length : actes.filter((a) => matchesFilter(a, f)).length;

  return (
    <div className="bg-white border border-background-200/60 rounded-xl overflow-hidden">
      {/* En-tête */}
      <div className="px-5 py-4 border-b border-background-100 flex items-center gap-2.5">
        <i className="ri-flow-chart text-primary-500 text-lg"></i>
        <h3 className="text-sm font-bold text-foreground-800">Panneau de règles NGAP</h3>
        <span className="text-xs text-foreground-400 ml-auto">
          Cliquez sur un acte pour voir pourquoi
        </span>
      </div>

      {/* Filtres */}
      <div className="px-5 py-3 border-b border-background-100 flex items-center gap-1.5 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              filter === f.id
                ? 'bg-primary-500 text-white'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {f.label}
            <span className={`ml-1 ${filter === f.id ? 'text-white/70' : 'text-foreground-400'}`}>
              {countFor(f.id)}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* ─── Liste des actes ─── */}
        <div className="border-b lg:border-b-0 lg:border-r border-background-100 max-h-[520px] overflow-y-auto">
          {sortedPeriods.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <i className="ri-filter-3-line text-foreground-300 text-2xl"></i>
              <p className="text-xs text-foreground-400 mt-2">Aucun acte ne correspond à ce filtre.</p>
            </div>
          ) : (
            sortedPeriods.map((periode) => {
              const meta = PERIOD_META[periode] || PERIOD_META.matin;
              return (
                <div key={periode}>
                  <div className={`px-5 py-2 bg-background-50/60 flex items-center gap-2 border-b ${meta.border}`}>
                    <span className="text-sm">{meta.emoji}</span>
                    <span className={`text-xs font-bold ${meta.text} capitalize`}>{periode}</span>
                  </div>
                  <div className="divide-y divide-background-100/60">
                    {grouped[periode].map((a) => {
                      const info = rateInfo(a);
                      const isSelected = keyOf(a) === (selected ? keyOf(selected) : '');
                      const k = keyOf(a);
                      return (
                        <button
                          key={k}
                          onClick={() => setSelectedId(k)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${
                            isSelected ? 'bg-primary-50/70 border-l-2 border-l-primary-500' : 'hover:bg-background-50 border-l-2 border-l-transparent'
                          }`}
                        >
                          <span className={`inline-block px-2 py-0.5 text-[11px] font-bold rounded whitespace-nowrap ${
                            a.inclusDansForfait
                              ? 'bg-foreground-100 text-foreground-400'
                              : 'bg-primary-100 text-primary-700'
                          }`}>
                            {a.codeNGAP}
                          </span>
                          <span className={`flex-1 min-w-0 text-xs font-medium truncate ${
                            a.inclusDansForfait ? 'text-foreground-400 line-through' : 'text-foreground-800'
                          }`}>
                            {a.acte.acte.label}
                          </span>
                          <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded ${info.badge}`}>
                            {info.label}
                          </span>
                          <i className={`flex-shrink-0 text-xs text-foreground-300 ${isSelected ? 'ri-arrow-right-s-line text-primary-500' : 'ri-arrow-down-s-line'}`}></i>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ─── Détail du raisonnement ─── */}
        <div className="p-5">
          {selected ? (
            <div>
              {/* En-tête acte */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="inline-block px-2 py-0.5 text-xs font-bold rounded bg-primary-100 text-primary-700">
                      {selected.codeNGAP}
                    </span>
                    {selected.exceptionAppliquee && (
                      <span className="text-[10px] font-semibold text-accent-700 bg-accent-50 px-1.5 py-0.5 rounded border border-accent-200/40">
                        {selected.exceptionAppliquee}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-bold text-foreground-900">
                    {selected.acte.acte.label}
                  </h4>
                  <p className="text-[11px] text-foreground-400 mt-0.5">
                    Coef. {selected.coefficient} — {selected.article}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`inline-flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 rounded-lg ${rateInfo(selected).badge}`}>
                    <span className={`w-2 h-2 rounded-full ${rateInfo(selected).dot}`}></span>
                    {rateInfo(selected).label}
                  </span>
                  <span className={`block text-lg font-bold mt-1 ${
                    selected.inclusDansForfait || selected.tauxApplique === 0 ? 'text-foreground-300' : 'text-foreground-900'
                  }`}>
                    {selected.montantFinal.toFixed(2).replace('.', ',')} €
                  </span>
                </div>
              </div>

              {/* Pourquoi ce taux ? */}
              <div className="bg-accent-50/60 border border-accent-200/40 rounded-lg p-3.5 mb-4">
                <p className="text-xs font-bold text-accent-800 flex items-center gap-1.5 mb-1">
                  <i className="ri-question-line text-sm"></i>
                  Pourquoi ce taux ?
                </p>
                <p className="text-xs text-foreground-700 leading-relaxed">
                  {decisionPhrase(selected)}
                </p>
              </div>

              {/* Justification complète */}
              {selected.raison && (
                <div className="mb-4">
                  <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-1.5">
                    Justification détaillée
                  </p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {selected.raison}
                  </p>
                </div>
              )}

              {/* Pipeline de décision */}
              <div className="mb-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-2.5">
                  Chemin de décision
                </p>
                <div className="space-y-0">
                  {buildTrace(selected).map((step, i, arr) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center">
                          <i className={`${step.icon} ${step.color} text-sm`}></i>
                        </div>
                        {i < arr.length - 1 && <div className="w-px flex-1 bg-background-200 my-1"></div>}
                      </div>
                      <div className="pb-3 pt-1 flex-1">
                        <p className="text-xs font-semibold text-foreground-800">{step.titre}</p>
                        <p className="text-[11px] text-foreground-500 leading-relaxed">{step.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Règle de cumul */}
              {selected.regleCumul && (
                <div className="flex items-start gap-2 bg-background-50 border border-background-200/50 rounded-lg p-3">
                  <i className="ri-bookmark-line text-secondary-500 text-sm mt-0.5"></i>
                  <div>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-0.5">
                      Règle appliquée
                    </p>
                    <p className="text-xs text-foreground-700 leading-relaxed">{selected.regleCumul}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-mouse-line text-foreground-300 text-3xl"></i>
              <p className="text-sm text-foreground-400 mt-3">Sélectionnez un acte</p>
              <p className="text-xs text-foreground-300 mt-1">pour afficher son raisonnement de cotation.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}